/**
 * `Assets_Registry.ts` — port of `AI/Registry/Assets_Registry.cs`.
 *
 * Web slice: `copy_model` / `paste_model` / `Get_Objects_From_Library` /
 * `Import_from_Library` wait on the duplication + asset-library ports and
 * are not registered. `Update_Material_properties` is registered but stays
 * disabled for agents (Unity `s_toolsDisabledForAgents` parity).
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  buildParams,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addAssetsTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.GetPartMaterials,
      category: "Context",
      labelWhenActive: "Reading materials...",
      description:
        "Returns the materials applied to a part and its children by hierarchy path: material ids, display names, shader family, and base color. Use before answering material questions or choosing a library material to apply. Resolve targets first (see system prompt).",
      humanDescription: "Inspect the materials on a part and its children.",
      parameters: buildParams({
        partPath: stringParam("Hierarchy path from Get_Scene_Context (e.g. '0,1,2').", false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GetMaterialLibrary,
      category: "Context",
      labelWhenActive: "Browsing material library...",
      description:
        "Returns the material library grouped by category with material names. Use to pick the closest matching library material before apply_material_on_parts (e.g. for 'make it red / metallic / wood' requests). Optionally narrow with a short prompt.",
      humanDescription: "Browse the material library to find a material to apply.",
      parameters: buildParams({
        prompt: stringParam(
          "Short query to narrow the library listing, e.g. 'red', 'metal', 'wood'.",
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ApplyMaterialOnParts,
      category: "Authoring",
      labelWhenActive: "Applying material...",
      description: `Applies a material from the material library to one or more parts. materialId must be an exact library material name from ${AgentToolNames.GetMaterialLibrary}. Resolve part targets first (see system prompt). This is the preferred tool for 'change color / make it red / metallic / wood' requests.`,
      humanDescription: "Apply a library material to one or more parts.",
      parameters: buildParams({
        materialId: stringParam(
          "Exact library material name from Get_Material_Library.",
          false,
        ),
        partPaths: stringList("Hierarchy paths from Get_Scene_Context (e.g. '0,1,2')."),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.DeleteModel,
      category: "Authoring",
      labelWhenActive: "Deleting model...",
      description:
        "Deletes one or more whole top-level models from the scene permanently. Operates only on root models: passing a part path within a model auto-resolves to its root model. Use for 'delete/remove/get rid of/erase' model requests. Do NOT use for hiding - use hide_parts for visibility. Resolve targets first (see system prompt).",
      humanDescription: "Permanently delete whole models from the scene.",
      parameters: buildParams({
        modelPaths: stringList(
          "Hierarchy paths identifying target model(s) (any part path is auto-resolved to its root model).",
        ),
        modelName: stringParam(
          "Display name of the model to delete (only when no path is available; must match exactly one model).",
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
  );
}
